import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, ExternalLink, Check, AlertCircle, LogIn } from "lucide-react";
import { api, apiError } from "@/lib/api";
import EarnKaroQueue from "@/pages/admin/EarnKaroQueue";
import EarnKaroCatalog from "@/pages/admin/EarnKaroCatalog";
import DealsAdmin from "@/pages/admin/DealsAdmin";
import { useAuth } from "@/context/AuthContext";
import { MarketLogo } from "@/components/ConnectPrimaryCard";
import { toast } from "sonner";

const STATUS_LABEL = {
  openai: "OpenAI",
  anthropic: "Anthropic",
  google: "Google",
  legacy: "Legacy",
  stripe: "Stripe",
  local: "Local disk",
  cloud: "Cloud",
  on: "On",
  off: "Off",
  missing: "Not set",
  smtp: "SMTP",
  resend: "Resend",
};

function StatusPill({ value }) {
  const ok = value && value !== "missing" && value !== "off";
  return (
    <span className={`text-[11px] uppercase tracking-luxe ${ok ? "text-aura-accent" : "text-aura-muted"}`}>
      {ok ? <Check className="w-3 h-3 inline mr-1" /> : <AlertCircle className="w-3 h-3 inline mr-1" />}
      {STATUS_LABEL[value] || value}
    </span>
  );
}

const TABS = [
  { id: "catalog", label: "Catalog & affiliates" },
  { id: "marketplaces", label: "Seller marketplaces" },
  { id: "platform", label: "Platform" },
  { id: "requests", label: "Requests" },
  { id: "earnkaro", label: "EarnKaro" },
  { id: "deals", label: "Deals" },
];

const NETWORKS = [
  { id: "rakuten", label: "Rakuten", mark: "Rk", color: "#BF0000", blurb: "Product Search and deep links. Only advertisers you are approved for return products." },
  { id: "cj", label: "CJ", mark: "CJ", color: "#00A3E0", blurb: "GraphQL product feed. Only advertisers you joined return products." },
  { id: "awin", label: "Awin", mark: "Aw", color: "#FF6A00", blurb: "Joined offers and tracking links. Awin has no live keyword product search." },
  { id: "flexoffers", label: "FlexOffers", mark: "Fx", color: "#1B4F9C", blurb: "Publisher product catalog. Deep links in the response carry commission." },
  { id: "involve_asia", label: "Involve Asia", mark: "Iv", color: "#6D28D9", blurb: "Approved offers in South-East Asia. A click on that store is turned into an invol.co link. There is no product catalog." },
  { id: "amazon_associates", label: "Amazon Associates", mark: "Az", color: "#FF9900", blurb: "PA-API catalog search. The Associate tag is on every buy link. Separate from the seller SP-API app." },
  { id: "ebay_browse", label: "eBay Partner Network", mark: "eB", color: "#E53238", blurb: "Campaign ID and partner tokens for commission links. The seller app keys are on Seller marketplaces." },
  { id: "walmart_affiliate", label: "Walmart Affiliates", mark: "W", color: "#0071CE", blurb: "Walmart.io product search. Separate from the Shopify seller app." },
  { id: "skimlinks", label: "Skimlinks", mark: "Sk", color: "#5B2C83", blurb: "Product API across merchants in your Skimlinks account. Links are already tracked." },
  { id: "target", label: "Target", mark: "T", color: "#CC0000", blurb: "Target catalog through your Impact publisher account. Tracking URLs carry commission." },
  { id: "bestbuy", label: "Best Buy", mark: "BB", color: "#0046BE", blurb: "Best Buy Products API. The optional affiliate ref is appended to product URLs." },
  { id: "admitad", label: "Admitad", mark: "Ad", color: "#6C2BD9", blurb: "Joined programs, coupons, and product-feed excerpts. Join the India programs you want in Admitad first." },
  { id: "flipkart_affiliate", label: "Flipkart Affiliate", mark: "Fk", color: "#2874F0", blurb: "Flipkart product search. Buy links include your tracking ID. Separate from the Flipkart seller app." },
  { id: "cuelinks", label: "Cuelinks", mark: "Cu", color: "#E11D48", blurb: "Converts a plain India store link into a tracked link on the click. A store pays only after that campaign is approved." },
  { id: "quickcommerce", label: "QuickCommerce", mark: "QC", color: "#0F766E", blurb: "Live India prices. A saved API key searches every QuickCommerce store. Each store spends one credit per search." },
  { id: "serper", label: "Serper", mark: "Se", color: "#111111", blurb: "Google results for India store discovery. Paste the API key from serper.dev, then use Test connection." },
  { id: "serpapi", label: "SerpApi", mark: "Sa", color: "#1E40AF", blurb: "Backup for Walmart, Amazon US and Amazon.in when their own pages fail. Capped per day; pre-collection never uses it." },
  { id: "decodo", label: "Decodo proxy", mark: "Dc", color: "#334155", blurb: "Residential proxy for India and US store reads. Paste the shared pool URL; India and US pools are optional overrides." },
  { id: "jina", label: "Jina Reader", mark: "Ji", color: "#0F172A", blurb: "Backup page reader for stores that block the proxy. Without a key it runs at about 20 reads a minute." },
];

const SELLER_APP_IDS = new Set([
  "ebay_client_id", "ebay_client_secret", "ebay_ru_name", "ebay_deletion_verification_token", "ebay_deletion_endpoint",
  "etsy_api_key", "etsy_client_secret",
  "depop_client_id", "depop_client_secret",
  "amazon_lwa_client_id", "amazon_lwa_client_secret", "amazon_app_id",
  "walmart_client_id", "walmart_client_secret", "walmart_channel_type",
  "flipkart_client_id", "flipkart_client_secret",
  "wix_app_id", "wix_app_secret", "wix_share_url_id",
  "square_app_id", "square_app_secret",
  "squarespace_client_id", "squarespace_client_secret",
  "google_merchant_client_id", "google_merchant_client_secret",
  "bonanza_dev_id", "bonanza_cert_id",
  "stockx_client_id", "stockx_client_secret", "stockx_api_key",
  "backmarket_token", "goat_access_token",
  "pinterest_app_id", "pinterest_app_secret",
]);

const SELLER_MARKETS = [
  { id: "ebay", name: "eBay", where: "https://developer.ebay.com/my/keys", blurb: "Paste the Shoptosa app. Sellers then click Connect.", fields: ["ebay_client_id", "ebay_client_secret", "ebay_ru_name", "ebay_deletion_verification_token", "ebay_deletion_endpoint"] },
  { id: "etsy", name: "Etsy", where: "https://www.etsy.com/developers/your-apps", blurb: "Paste the Shoptosa app. Sellers then click Connect.", fields: ["etsy_api_key", "etsy_client_secret"] },
  { id: "depop", name: "Depop", where: "https://partnerapi.depop.com/api-docs/", blurb: "Invite-only app. Sellers click Connect after these keys are saved.", fields: ["depop_client_id", "depop_client_secret"] },
  { id: "amazon", name: "Amazon.com", where: "https://sellercentral.amazon.com/sellingpartner/developerconsole", blurb: "One SP-API app for Amazon.com and Amazon.in. Both stay coming soon for sellers until testing is finished.", fields: ["amazon_lwa_client_id", "amazon_lwa_client_secret", "amazon_app_id"] },
  { id: "amazon_in", name: "Amazon.in", where: "https://sellercentral.amazon.in/", blurb: "Coming soon for sellers until Amazon.in testing is finished. Uses the Amazon app above.", fields: [], soon: true },
  { id: "walmart", name: "Shopify", where: "https://dev.shopify.com/", blurb: "Dev Dashboard app keys. Each seller enters their store and approves Shoptosa.", fields: ["walmart_client_id", "walmart_client_secret", "walmart_channel_type"] },
  { id: "flipkart", name: "Flipkart", where: "https://seller.flipkart.com/", blurb: "Approved third-party app. Paste the Application ID and secret. Sellers then click Connect.", fields: ["flipkart_client_id", "flipkart_client_secret"] },
  { id: "wix", name: "Wix", where: "https://manage.wix.com/apps", blurb: "Create a Shoptosa app with Wix Stores permission. Sellers click Connect and approve their site.", fields: ["wix_app_id", "wix_app_secret", "wix_share_url_id"] },
  { id: "pinterest", name: "Pinterest", where: "https://developers.pinterest.com/apps/", blurb: "One Shoptosa Pinterest app. Sellers click Connect after catalog write is approved.", fields: ["pinterest_app_id", "pinterest_app_secret"] },
  { id: "woocommerce", name: "WooCommerce", seller: "No Admin key. The seller enters the store URL and approves Shoptosa." },
  { id: "bonanza", name: "Bonanza", where: "https://api.bonanza.com/docs", blurb: "One Shoptosa developer account. Sellers click Connect and approve their booth.", fields: ["bonanza_dev_id", "bonanza_cert_id"] },
  { id: "backmarket", name: "Back Market", where: "https://www.backmarket.com/", blurb: "Token for the Shoptosa Back Market account. Sellers click Connect, then Post.", fields: ["backmarket_token"] },
  { id: "stockx", name: "StockX", where: "https://developer.stockx.com/portal", blurb: "One Shoptosa StockX app. Sellers click Connect and allow their account.", fields: ["stockx_client_id", "stockx_client_secret", "stockx_api_key"] },
  { id: "goat", name: "GOAT", where: "https://alias.org/", blurb: "Alias token for the Shoptosa account. Sellers click Connect, then Post.", fields: ["goat_access_token"] },
  { id: "squarespace", name: "Squarespace", where: "https://developers.squarespace.com/oauth", blurb: "One Shoptosa OAuth client. Sellers click Connect and pick their site.", fields: ["squarespace_client_id", "squarespace_client_secret"] },
  { id: "square", name: "Square", where: "https://developer.squareup.com/apps", blurb: "One Shoptosa Square app. Sellers click Connect and approve their account.", fields: ["square_app_id", "square_app_secret"] },
  { id: "google_shopping", name: "Google Shopping", where: "https://console.cloud.google.com/apis/credentials", blurb: "Coming soon for sellers. Google app review is not finished.", fields: ["google_merchant_client_id", "google_merchant_client_secret"], soon: true },
  { id: "poshmark", name: "Poshmark", seller: "The seller lists with the Chrome helper. No admin key." },
  { id: "mercari", name: "Mercari", seller: "The seller lists with the Chrome helper. No admin key." },
  { id: "grailed", name: "Grailed", seller: "The seller lists with the Chrome helper. No admin key." },
  { id: "facebook", name: "Facebook Marketplace", seller: "The seller lists with the Chrome helper. No admin key." },
  { id: "meesho", name: "Meesho", soon: true, seller: "Coming soon. No admin key yet." },
  { id: "myntra", name: "Myntra", soon: true, seller: "Coming soon. No admin key yet." },
  { id: "ajio", name: "AJIO", soon: true, seller: "Coming soon. No admin key yet." },
  { id: "nykaa", name: "Nykaa Fashion", soon: true, seller: "Coming soon. No admin key yet." },
  { id: "tatacliq", name: "Tata CLiQ", soon: true, seller: "Coming soon. No admin key yet." },
  { id: "olx", name: "OLX", soon: true, seller: "Coming soon. No admin key yet." },
];

const PLATFORM_LOOK = {
  "AI — pick one text provider": { title: "Text AI", mark: "AI", color: "#111111" },
  "AI — optional fallback": { title: "AI fallback", mark: "AI", color: "#444444" },
  Models: { title: "Models", mark: "Md", color: "#0F766E" },
  Payments: { title: "Payments", mark: "Pay", color: "#0B6E4F" },
  Email: { title: "Email", mark: "Mail", color: "#1D4ED8" },
  Storefront: { title: "Storefront", mark: "Web", color: "#111111" },
  "Seller SaaS": { title: "Seller plans", mark: "Plan", color: "#7C3AED" },
  Graph: { title: "Catalog graph", mark: "G", color: "#B45309" },
  Locale: { title: "Locale", mark: "IN", color: "#0369A1" },
};

function Mark({ mark, color }) {
  return (
    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-[11px] font-semibold text-white" style={{ background: color }}>
      {mark}
    </span>
  );
}

function sectionOf(group = "") {
  if (group.startsWith("Catalog")) return "catalog";
  if (group.startsWith("Marketplace")) return "marketplaces";
  return "platform";
}

function FieldControl({ field, draft, setField, clearSecret }) {
  const value = field.secret ? (draft[field.id] || "") : (draft[field.id] ?? String(field.value ?? ""));
  const className = "flex-1 bg-aura-surface border border-aura-border px-3 py-2 text-sm outline-none w-full";
  return (
    <div className="mt-3">
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <label className="text-xs font-medium" htmlFor={field.id}>{field.label}</label>
        <span className={`text-[10px] uppercase tracking-luxe shrink-0 ${field.configured ? "text-aura-accent" : "text-aura-muted"}`}>
          {field.configured ? "Set" : "Missing"}
        </span>
      </div>
      <p className="text-[11px] text-aura-muted mb-1.5 leading-relaxed">{field.hint}</p>
      <div className="flex gap-2">
        {field.multiline ? (
          <textarea
            id={field.id}
            rows={5}
            placeholder={field.configured ? "Paste a new PEM to replace…" : "Paste PEM private key…"}
            value={value}
            onChange={(e) => setField(field.id, e.target.value)}
            className={`${className} font-mono text-xs`}
            data-testid={`setting-${field.id}`}
            autoComplete="off"
            spellCheck={false}
          />
        ) : (
          <input
            id={field.id}
            type={field.secret ? "password" : "text"}
            placeholder={field.secret ? (field.configured ? field.value : "Paste key…") : ""}
            value={value}
            onChange={(e) => setField(field.id, e.target.value)}
            className={className}
            data-testid={`setting-${field.id}`}
            autoComplete="off"
          />
        )}
        {field.secret && field.configured && (
          <button type="button" onClick={() => clearSecret(field.id)} className="text-xs text-aura-muted hover:text-aura-accent px-2 self-start">
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { impersonate } = useAuth();
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [testingMail, setTestingMail] = useState(false);
  const [testingNet, setTestingNet] = useState(null);
  const [tab, setTab] = useState("catalog");
  const [missingOnly, setMissingOnly] = useState(false);
  const [openingId, setOpeningId] = useState(null);

  const { data } = useQuery({
    queryKey: ["admin-settings"],
    queryFn: async () => (await api.get("/admin/settings")).data,
  });
  const { data: overview } = useQuery({
    queryKey: ["admin-overview"],
    queryFn: async () => (await api.get("/admin/overview")).data,
  });
  const { data: users = [] } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => (await api.get("/admin/users")).data,
  });
  const { data: payoutData } = useQuery({
    queryKey: ["admin-developer-payouts"],
    queryFn: async () => (await api.get("/admin/developer-payouts")).data,
  });
  const payoutsAutomatic = !!payoutData?.payouts_automatic;
  const payoutRequests = useMemo(() => {
    const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString();
    return (payoutData?.requests || []).filter((r) => !["paid", "failed"].includes(r.status) || (r.updated_at || "") > weekAgo);
  }, [payoutData]);

  const groups = useMemo(() => {
    const map = {};
    for (const f of data?.fields || []) {
      (map[f.group] ||= []).push(f);
    }
    return map;
  }, [data]);

  const setField = (id, value) => setDraft((d) => ({ ...d, [id]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      Object.entries(draft).forEach(([k, v]) => {
        if (v === "") return;
        patch[k] = v;
      });
      await api.put("/admin/settings", patch);
      setDraft({});
      qc.invalidateQueries({ queryKey: ["admin-settings"] });
      qc.invalidateQueries({ queryKey: ["admin-overview"] });
      toast.success("Settings saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const testNetwork = async (network) => {
    setTestingNet(network);
    try {
      if (Object.keys(draft).length) {
        toast.error("Save settings before testing");
        return;
      }
      const { data: result } = await api.post("/admin/settings/test-catalog", { network });
      const sample = result.sample ? ` Sample: ${result.sample}` : "";
      toast.success(result.detail ? `${result.detail}${sample}` : `Connected.${sample}`);
    } catch (e) {
      toast.error(apiError(e) || "Catalog test failed");
    } finally {
      setTestingNet(null);
    }
  };

  const testEmail = async () => {
    setTestingMail(true);
    try {
      const { data: result } = await api.post("/admin/settings/test-email");
      toast.success(`Test email sent to ${result.to}. Check inbox and Spam.`);
    } catch (e) {
      toast.error(apiError(e) || "Mailer test failed");
    } finally {
      setTestingMail(false);
    }
  };

  const clearSecret = async (id) => {
    try {
      await api.put("/admin/settings", { [id]: "__CLEAR__" });
      qc.invalidateQueries({ queryKey: ["admin-settings"] });
      toast.success("Key cleared");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setRole = async (id, role) => {
    try {
      await api.patch(`/admin/users/${id}`, { role });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("Role updated");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openAs = async (account) => {
    setOpeningId(account.id);
    try {
      const next = await impersonate(account.id);
      qc.clear();
      toast.success(`Viewing ${next.name}`);
      navigate(next.role === "seller" ? "/seller" : next.role === "developer" ? "/developers/console/wallet" : "/shop");
    } catch (e) {
      toast.error(apiError(e) || "Could not open that account");
    } finally {
      setOpeningId(null);
    }
  };

  const sellers = users.filter((u) => u.role === "seller");
  const developers = users.filter((u) => u.role === "developer");
  const others = users.filter((u) => u.role !== "seller" && u.role !== "developer");

  const grantCredits = async (account) => {
    const raw = window.prompt(`Credits to add to ${account.name}'s wallet (1 credit = ₹1)`, "100");
    const credits = Number(raw);
    if (!raw || !Number.isFinite(credits) || credits <= 0) return;
    setOpeningId(account.id);
    try {
      const { data: granted } = await api.post(`/admin/users/${account.id}/credits`, { credits });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success(`${account.name} now has ${granted.credits} credits`);
    } catch (e) {
      toast.error(apiError(e) || "Could not add credits");
    } finally {
      setOpeningId(null);
    }
  };

  const markPayoutPaid = async (request) => {
    if (request.automatic && !window.confirm("This withdrawal was sent through Cashfree. Only mark it paid if Cashfree shows it paid, or you paid it yourself after Cashfree failed.")) return;
    const reference = window.prompt("Bank or UPI reference (UTR) for this payout", "");
    if (reference === null) return;
    try {
      await api.post(`/admin/developer-payouts/${request.id}/paid`, { reference });
      qc.invalidateQueries({ queryKey: ["admin-developer-payouts"] });
      toast.success("Marked paid");
    } catch (e) {
      toast.error(apiError(e) || "Could not mark it paid");
    }
  };

  const payoutAction = async (request, action) => {
    setOpeningId(`${action}-${request.id}`);
    try {
      const { data: out } = await api.post(`/admin/developer-payouts/${request.id}/${action}`);
      qc.invalidateQueries({ queryKey: ["admin-developer-payouts"] });
      if (out.status === "paid") toast.success("Paid");
      else if (out.status === "failed") toast.error(`Payout failed: ${out.failure_reason || "see Cashfree"}`);
      else if (out.last_error) toast.error(out.last_error);
      else toast.success(action === "send" ? "Sent to Cashfree" : `Status: ${out.status}`);
    } catch (e) {
      toast.error(apiError(e) || "Cashfree did not answer");
    } finally {
      setOpeningId(null);
    }
  };

  const status = data?.status || overview?.status || {};

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 pt-28 pb-24">
      <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-2">Platform</div>
      <h1 className="font-serif text-4xl tracking-tight mb-3">Admin</h1>
      <p className="text-sm text-aura-muted max-w-2xl mb-8">
        {data?.note || "Configure API keys and platform settings."}
      </p>

      <div className="grid sm:grid-cols-4 gap-3 mb-8 text-sm">
        <div className="border border-aura-border p-4"><div className="text-aura-muted text-[11px] uppercase tracking-luxe">Users</div><div className="font-serif text-2xl mt-1">{overview?.users ?? "—"}</div></div>
        <div className="border border-aura-border p-4"><div className="text-aura-muted text-[11px] uppercase tracking-luxe">Sellers</div><div className="font-serif text-2xl mt-1">{overview?.sellers ?? "—"}</div></div>
        <div className="border border-aura-border p-4"><div className="text-aura-muted text-[11px] uppercase tracking-luxe">Listings</div><div className="font-serif text-2xl mt-1">{overview?.products ?? "—"}</div></div>
        <div className="border border-aura-border p-4"><div className="text-aura-muted text-[11px] uppercase tracking-luxe">API keys</div><div className="font-serif text-2xl mt-1">{overview?.api_keys ?? "—"}</div></div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={`px-4 py-1.5 text-xs rounded-full border ${tab === item.id ? "btn-accent border-transparent" : "border-aura-border text-aura-muted"}`}
            data-testid={`admin-tab-${item.id}`}
          >
            {item.label}
          </button>
        ))}
        {tab !== "platform" && tab !== "requests" && tab !== "earnkaro" && tab !== "deals" && (
          <button
            type="button"
            onClick={() => setMissingOnly((v) => !v)}
            className={`px-4 py-1.5 text-xs rounded-full border ${missingOnly ? "border-aura-accent text-aura-accent" : "border-aura-border text-aura-muted"}`}
          >
            {missingOnly ? "Showing missing" : "Missing only"}
          </button>
        )}
      </div>

      {tab === "catalog" && (
        <div className="grid lg:grid-cols-2 gap-4 mb-8">
          {NETWORKS.map((net) => {
            const fields = (data?.fields || []).filter((f) => f.network === net.id && !SELLER_APP_IDS.has(f.id));
            if (missingOnly && status[net.id] === "on") return null;
            const where = fields.find((f) => f.where)?.where;
            return (
              <div key={net.id} className="border border-aura-border rounded-2xl p-5 bg-aura-surface" data-testid={`network-${net.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <Mark mark={net.mark} color={net.color} />
                    <div>
                      <div className="font-serif text-xl tracking-tight">{net.label}</div>
                      <p className="text-xs text-aura-muted mt-1 leading-relaxed">{net.blurb}</p>
                    </div>
                  </div>
                  <StatusPill value={status[net.id] || "off"} />
                </div>
                {where && (
                  <a href={where} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] link-underline mt-3">
                    Developer portal <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {fields.map((f) => (
                  <FieldControl key={f.id} field={f} draft={draft} setField={setField} clearSecret={clearSecret} />
                ))}
                <button
                  type="button"
                  onClick={() => testNetwork(net.id)}
                  disabled={testingNet === net.id || status[net.id] !== "on"}
                  className="mt-4 px-4 py-1.5 border border-aura-border text-xs rounded-full disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid={`test-network-${net.id}`}
                >
                  {testingNet === net.id && <Loader2 className="w-3 h-3 animate-spin" />}
                  Test connection
                </button>
              </div>
            );
          })}
          {(() => {
            const shared = (data?.fields || []).filter((f) => f.group === "Catalog networks" && !f.network);
            if (!shared.length) return null;
            if (missingOnly && shared.every((f) => f.configured)) return null;
            return (
              <div className="border border-aura-border rounded-2xl p-5 bg-aura-surface lg:col-span-2" data-testid="network-shared">
                <div className="flex items-start gap-3">
                  <Mark mark="All" color="#111111" />
                  <div>
                    <div className="font-serif text-xl tracking-tight">Shared affiliate settings</div>
                    <p className="text-xs text-aura-muted mt-1 leading-relaxed">Used across catalog networks, not tied to one merchant.</p>
                  </div>
                </div>
                {shared.map((f) => (
                  <FieldControl key={f.id} field={f} draft={draft} setField={setField} clearSecret={clearSecret} />
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {tab !== "catalog" && tab !== "earnkaro" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          {(tab === "platform"
            ? [["Text AI", status.text_ai], ["Images", status.image_ai], ["Checkout", status.payments], ["Storage", status.storage], ["Mailer", status.mailer], ["Channel3", status.external_catalog]]
            : [["Connector", status.commerce]]
          ).map(([label, val]) => (
            <div key={label} className="border border-aura-border p-4">
              <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-1">{label}</div>
              <StatusPill value={val} />
            </div>
          ))}
        </div>
      )}

      {tab === "marketplaces" && (
        <div className="mb-8">
          <p className="text-sm text-aura-muted mb-4 max-w-3xl">
            App keys for shops where sellers click Connect. Shops that say “Seller connects” keep the token on that seller’s Marketplaces page, not here.
          </p>
          {(data?.fields || []).filter((f) => f.id === "commerce_connector").map((f) => (
            <div key={f.id} className="border border-aura-border rounded-2xl p-5 bg-aura-surface mb-4">
              <FieldControl field={f} draft={draft} setField={setField} clearSecret={clearSecret} />
            </div>
          ))}
          <div className="grid lg:grid-cols-2 gap-4">
            {SELLER_MARKETS.map((shop) => {
              const fields = (shop.fields || []).map((id) => (data?.fields || []).find((f) => f.id === id)).filter(Boolean);
              const ready = fields.length > 0 && fields.every((f) => f.configured);
              const missing = fields.some((f) => !f.configured);
              if (missingOnly && !missing) return null;
              const badge = shop.badge || (shop.soon ? "Coming soon" : (fields.length ? (ready ? "Keys saved" : "Needs keys") : "Seller connects"));
              const where = shop.where || fields.find((f) => f.where)?.where;
              return (
                <div key={shop.id} className="border border-aura-border rounded-2xl p-5 bg-aura-surface" data-testid={`admin-market-${shop.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white">
                        <MarketLogo id={shop.id} />
                      </span>
                      <div className="min-w-0">
                        <div className="font-serif text-xl tracking-tight">{shop.name}</div>
                        <p className="text-xs text-aura-muted mt-1 leading-relaxed">{shop.blurb || shop.seller}</p>
                      </div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-luxe shrink-0 ${badge === "Needs keys" || badge === "Coming soon" ? "text-aura-muted" : "text-aura-accent"}`}>
                      {badge}
                    </span>
                  </div>
                  {where && fields.length > 0 && (
                    <a href={where} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] link-underline mt-3">
                      Developer portal <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {fields.map((f) => (
                    <FieldControl key={f.id} field={f} draft={draft} setField={setField} clearSecret={clearSecret} />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "platform" && Object.entries(groups).filter(([group]) => sectionOf(group) === "platform").map(([group, fields]) => {
        const look = PLATFORM_LOOK[group] || { title: group, mark: group.slice(0, 2), color: "#111111" };
        return (
          <div key={group} className="mb-4 border border-aura-border rounded-2xl p-5 bg-aura-surface">
            <div className="flex items-center gap-3 mb-1">
              <Mark mark={look.mark} color={look.color} />
              <div className="font-serif text-xl tracking-tight">{look.title}</div>
            </div>
            {fields.map((f) => (
              <div key={f.id}>
                <FieldControl field={f} draft={draft} setField={setField} clearSecret={clearSecret} />
                {f.where && (
                  <a href={f.where} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] link-underline mt-2">
                    Get this key <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        );
      })}

      {tab === "requests" && <CatalogRequests />}

      {tab === "earnkaro" && (
        <>
          <EarnKaroCatalog />
          <EarnKaroQueue />
        </>
      )}

      {tab === "deals" && <DealsAdmin />}

      {tab !== "requests" && tab !== "earnkaro" && tab !== "deals" && <div className="flex flex-wrap gap-3 sticky bottom-4 bg-aura-bg/90 backdrop-blur py-3">
        <button
          onClick={save}
          disabled={saving}
          className="px-6 py-2.5 btn-accent text-sm rounded-full disabled:opacity-60 flex items-center gap-2"
          data-testid="save-settings"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          Save settings
        </button>
        {tab === "platform" && (
          <button
            type="button"
            onClick={testEmail}
            disabled={testingMail}
            className="px-6 py-2.5 border border-aura-border text-sm rounded-full disabled:opacity-60 flex items-center gap-2"
            data-testid="test-email"
          >
            {testingMail && <Loader2 className="w-4 h-4 animate-spin" />}
            Send test email
          </button>
        )}
      </div>}

      <div className="mt-16">
        <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-3">Sellers</div>
        <p className="text-sm text-aura-muted mb-4">Open a seller studio as that account. You can exit back to admin from the pink bar.</p>
        <div className="border border-aura-border divide-y divide-aura-border">
          {sellers.length === 0 ? (
            <div className="px-5 py-4 text-sm text-aura-muted">No sellers yet.</div>
          ) : sellers.map((u) => (
            <div key={u.id} className="flex items-center justify-between gap-4 px-5 py-3">
              <div>
                <div className="text-sm">{u.name}</div>
                <div className="text-xs text-aura-muted">{u.email} · {u.listing_count || 0} listings</div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={u.role}
                  onChange={(e) => setRole(u.id, e.target.value)}
                  className="bg-aura-surface border border-aura-border px-2 py-1 text-xs outline-none"
                >
                  <option value="admin">admin</option>
                  <option value="seller">seller</option>
                  <option value="buyer">buyer</option>
                  <option value="developer">developer</option>
                </select>
                <button
                  type="button"
                  onClick={() => openAs(u)}
                  disabled={openingId === u.id}
                  data-testid={`open-as-${u.id}`}
                  className="text-xs px-3 py-1.5 btn-accent rounded-full flex items-center gap-1.5 disabled:opacity-60"
                >
                  {openingId === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <LogIn className="w-3 h-3" />}
                  Open studio
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-12" data-testid="admin-developers">
        <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-3">Developers</div>
        <p className="text-sm text-aura-muted mb-4">
          Every developer has one credit wallet. Add credits here, or open the console as the developer to see their wallet.
        </p>
        <div className="border border-aura-border divide-y divide-aura-border">
          {developers.length === 0 ? (
            <div className="px-5 py-4 text-sm text-aura-muted">No developer accounts yet. Add credits to an account below to make it a developer.</div>
          ) : developers.map((u) => (
            <div key={u.id} className="flex items-center justify-between gap-4 px-5 py-3">
              <div>
                <div className="text-sm">{u.name}</div>
                <div className="text-xs text-aura-muted">
                  {u.email}
                  {u.credits == null ? " · wallet not opened yet" : ` · ${u.credits} credits`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={u.role}
                  onChange={(e) => setRole(u.id, e.target.value)}
                  className="bg-aura-surface border border-aura-border px-2 py-1 text-xs outline-none"
                >
                  <option value="admin">admin</option>
                  <option value="seller">seller</option>
                  <option value="buyer">buyer</option>
                  <option value="developer">developer</option>
                </select>
                <button
                  type="button"
                  onClick={() => grantCredits(u)}
                  disabled={openingId === u.id}
                  className="text-xs px-3 py-1.5 border border-aura-border rounded-full hover:border-aura-accent disabled:opacity-60"
                >
                  Add credits
                </button>
                <button
                  type="button"
                  onClick={() => openAs(u)}
                  disabled={openingId === u.id}
                  data-testid={`open-developer-${u.id}`}
                  className="text-xs px-3 py-1.5 btn-accent rounded-full flex items-center gap-1.5 disabled:opacity-60"
                >
                  {openingId === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <LogIn className="w-3 h-3" />}
                  Open console
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-12" data-testid="admin-developer-payouts">
        <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-3">Developer withdrawals</div>
        <p className="text-sm text-aura-muted mb-4">
          {payoutsAutomatic
            ? "Withdrawals go out through Cashfree Payouts as soon as a developer asks. Use Send to retry one Cashfree refused (for example, a low Payouts balance), or Check status if a webhook was missed."
            : "Cashfree Payouts keys are not saved, so pay each request to the UPI ID or bank account shown, then mark it paid with the transfer reference."}
        </p>
        <div className="border border-aura-border divide-y divide-aura-border">
          {payoutRequests.length === 0 ? (
            <div className="px-5 py-4 text-sm text-aura-muted">No withdrawal requests.</div>
          ) : payoutRequests.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-4 px-5 py-3">
              <div className="text-sm">
                <div>
                  {r.totals.map((t) => `${t.currency} ${Number(t.commission).toFixed(2)}`).join(", ")}
                  <span className="text-aura-muted"> · {r.email} · {r.sales} sale{r.sales === 1 ? "" : "s"}</span>
                </div>
                <div className="text-xs text-aura-muted font-mono mt-0.5">
                  {r.account?.method === "upi"
                    ? `UPI ${r.account.upi_id}`
                    : `A/c ${r.account?.account_number} · IFSC ${r.account?.ifsc}`}
                  {` · ${r.account?.holder_name || ""}`}
                </div>
                {r.status !== "paid" && (r.failure_reason || r.last_error || r.transfer_status) && (
                  <div className="text-xs text-aura-accent mt-0.5">
                    {[r.transfer_status, r.failure_reason || r.last_error].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
              {r.status === "paid" ? (
                <span className="text-xs text-aura-muted">Paid{r.reference ? ` · ${r.reference}` : ""}</span>
              ) : r.status === "failed" ? (
                <span className="text-xs text-aura-accent">Failed. Sales returned to the developer.</span>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-aura-muted capitalize">{r.status}</span>
                  {payoutsAutomatic && r.status === "requested" && (
                    <button
                      type="button"
                      disabled={!!openingId}
                      onClick={() => payoutAction(r, "send")}
                      className="text-xs px-3 py-1.5 border border-aura-border rounded-full disabled:opacity-60"
                      data-testid={`payout-send-${r.id}`}
                    >
                      {openingId === `send-${r.id}` ? "Sending…" : "Send via Cashfree"}
                    </button>
                  )}
                  {r.automatic && (
                    <button
                      type="button"
                      disabled={!!openingId}
                      onClick={() => payoutAction(r, "check")}
                      className="text-xs px-3 py-1.5 border border-aura-border rounded-full disabled:opacity-60"
                      data-testid={`payout-check-${r.id}`}
                    >
                      {openingId === `check-${r.id}` ? "Checking…" : "Check status"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => markPayoutPaid(r)}
                    className="text-xs px-3 py-1.5 btn-accent rounded-full"
                    data-testid={`payout-paid-${r.id}`}
                  >
                    Mark paid
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-12">
        <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-3">Other users</div>
        <div className="border border-aura-border divide-y divide-aura-border">
          {others.map((u) => (
            <div key={u.id} className="flex items-center justify-between gap-4 px-5 py-3">
              <div>
                <div className="text-sm">{u.name}</div>
                <div className="text-xs text-aura-muted">
                  {u.email}
                  {u.credits != null ? ` · ${u.credits} credits` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={u.role}
                  onChange={(e) => setRole(u.id, e.target.value)}
                  className="bg-aura-surface border border-aura-border px-2 py-1 text-xs outline-none"
                >
                  <option value="admin">admin</option>
                  <option value="seller">seller</option>
                  <option value="buyer">buyer</option>
                  <option value="developer">developer</option>
                </select>
                {u.role !== "admin" && (
                  <button
                    type="button"
                    onClick={() => grantCredits(u)}
                    disabled={openingId === u.id}
                    className="text-xs px-3 py-1.5 border border-aura-border rounded-full hover:border-aura-accent disabled:opacity-60"
                  >
                    Add credits
                  </button>
                )}
                {u.role !== "admin" && (
                  <button
                    type="button"
                    onClick={() => openAs(u)}
                    disabled={openingId === u.id}
                    className="text-xs px-3 py-1.5 border border-aura-border rounded-full hover:border-aura-accent disabled:opacity-60"
                  >
                    {openingId === u.id ? "Opening…" : "View as user"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function when(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
}

function RequestTable({ title, rows, primary, testId }) {
  return (
    <div className="mb-8" data-testid={testId}>
      <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-3">{title}</div>
      <div className="border border-aura-border divide-y divide-aura-border">
        {rows.length === 0 ? (
          <div className="px-5 py-4 text-sm text-aura-muted">None yet.</div>
        ) : rows.map((row) => (
          <div key={row.id} className="px-5 py-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div className="text-sm font-medium">{primary(row)}</div>
              <div className="text-xs text-aura-muted">{when(row.created_at)}</div>
            </div>
            <div className="text-xs text-aura-muted mt-1">
              {row.account_name ? `${row.account_name} · ` : ""}{row.email || "Unknown account"}
            </div>
            {row.note ? <div className="text-sm mt-2">{row.note}</div> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function CatalogRequests() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-catalog-requests"],
    queryFn: async () => (await api.get("/admin/catalog-requests")).data,
  });
  if (isLoading) return <div className="h-40 shimmer mb-8" />;
  if (error) return <div className="border border-aura-border px-5 py-4 text-sm text-aura-accent mb-8">{apiError(error)}</div>;
  return (
    <div data-testid="admin-catalog-requests">
      <RequestTable
        title="Brand requests"
        testId="admin-brand-requests"
        rows={data?.brands || []}
        primary={(row) => row.website ? `${row.name} · ${row.website}` : row.name}
      />
      <RequestTable
        title="Website requests"
        testId="admin-website-requests"
        rows={data?.websites || []}
        primary={(row) => row.name ? `${row.domain} · ${row.name}` : row.domain}
      />
    </div>
  );
}
